import type {RefObject} from 'react';
import type {FileRecord} from '@reados/contracts';

export interface CourseVideoBinding {
  courseId:string;
  lessonId:string;
  videoFileId:string;
  duration:number;
  initialPosition:number;
  subtitle?:FileRecord|null;
}
export type VideoPlaybackEvent='loadedmetadata'|'timeupdate'|'seeked'|'pause'|'ended';
export interface VideoPlaybackPosition {position:number;duration:number}
/** Playback transport changes; the native Reader owns its video controls. */
export interface ReaderVideoTransport {
  url?:string;
  initialPosition:number;
  subtitleUrl?:string;
  elementRef?:RefObject<HTMLVideoElement|null>;
  onEvent?:(event:VideoPlaybackEvent,position:VideoPlaybackPosition)=>void;
  onReady?:(element:HTMLVideoElement)=>void;
  busy?:boolean;
  error?:string;
  onRetry?:()=>void;
}

/** Different retained Reader surfaces share one ordered course-player lane. */
export class CoursePlaybackQueue {
  private tail:Promise<void>=Promise.resolve();
  run(operation:()=>Promise<void>):Promise<void>{
    const next=this.tail.catch(()=>{}).then(operation);this.tail=next;return next;
  }
}

export async function boundedPlaybackRequest<T>(request:(signal:AbortSignal)=>Promise<T>,milliseconds=10_000):Promise<T>{
  const controller=new AbortController();
  const timeout=setTimeout(()=>controller.abort(),milliseconds);
  try{return await request(controller.signal);}
  finally{clearTimeout(timeout);}
}

export function validCourseVideoBinding(file:FileRecord|undefined,binding:CourseVideoBinding|undefined):binding is CourseVideoBinding {
  if(!file||file.storage!=='course'||file.kind!=='video'||!binding||binding.videoFileId!==file.id
    ||!Number.isFinite(binding.duration)||binding.duration<=0||!Number.isFinite(binding.initialPosition)||binding.initialPosition<0
    ||!file.sandboxPath.startsWith(`/workspace/courses/${binding.courseId}/media/`))return false;
  const subtitle=binding.subtitle;
  return !subtitle||(subtitle.storage==='course'&&subtitle.mimeType==='text/vtt'
    &&subtitle.sandboxPath===file.sandboxPath.replace(/\.mp4$/,'.vtt'));
}

/** Clear buffered playback as well as the URL when authorization is lost. */
export function stopReaderVideo(element:Pick<HTMLVideoElement,'pause'|'removeAttribute'|'load'>|null):void {
  if(!element)return;
  element.pause();element.removeAttribute('src');element.load();
}

export interface CourseProgressWrite {position:number;duration:number;completed?:boolean}
/** One source-bound, ordered progress writer; loading/reset events never write zero. */
export function courseProgressReporter(binding:CourseVideoBinding,write:(value:CourseProgressWrite)=>Promise<unknown>,now:()=>number=Date.now){
  let position=Math.min(binding.initialPosition,binding.duration),ready=false;
  let last:{position:number;at:number;completed:boolean}|undefined;
  let queue=Promise.resolve();
  const save=(force=false,completed=false)=>{
    if(!ready)return queue;
    const at=now();
    if(last&&last.position===position&&last.completed===completed)return queue;
    if(!force&&last&&at-last.at<8_000&&Math.abs(position-last.position)<15)return queue;
    const value={position,duration:binding.duration,...(completed?{completed:true}:{})};
    const receipt={position,at,completed};last=receipt;
    queue=queue.catch(()=>{}).then(()=>write(value)).then(()=>{}).catch(()=>{if(last===receipt)last=undefined;});
    return queue;
  };
  return {
    get position(){return position;},
    event(event:VideoPlaybackEvent,value:VideoPlaybackPosition){
      if(!Number.isFinite(value.position)||value.position<0||value.position>binding.duration+2)return;
      if(event==='loadedmetadata'){ready=true;position=Math.min(value.position,binding.duration);return;}
      if(!ready)return;
      position=Math.min(value.position,binding.duration);
      void save(event!=='timeupdate',event==='ended');
    },
    flush:()=>save(true,last?.position===position&&last.completed),
    suspend(){const pending=save(true,last?.position===position&&last.completed);ready=false;return pending;},
  };
}

/** One page serializes lease acquisition and release across retained Reader tabs. */
export function createCoursePlaybackQueue(timeoutMs=15_000){
  let tail:Promise<unknown>=Promise.resolve();
  return {run<T>(operation:(signal:AbortSignal)=>Promise<T>):Promise<T>{
    const result=tail.catch(()=>{}).then(async()=>{
      const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),timeoutMs);
      try{return await operation(controller.signal);}finally{clearTimeout(timer);}
    });
    tail=result;return result;
  }};
}
export const coursePlaybackQueue=createCoursePlaybackQueue();
